/** Forecast weather vector tiles — one daily slice per valid_at date. */

import type { FillLayerSpecification, ExpressionSpecification } from 'mapbox-gl';
import {
  buildInterpolateFillColor,
  type ChoroplethColorStop,
  type ChoroplethLegendSpec,
} from './choroplethLegend';

export const FORECAST_TILE_MIN_ZOOM = 6;
export const FORECAST_TILE_MAX_ZOOM = 13;
export const FORECAST_LAYER_MAX_ZOOM = 22;

export type ForecastWeatherLayerKey =
  | 'forecastWeatherTemp'
  | 'forecastWeatherPrecip'
  | 'forecastWeatherWind';

export type ForecastWeatherPeriodKey =
  | 'forecastWeatherTempPeriods'
  | 'forecastWeatherPrecipPeriods'
  | 'forecastWeatherWindPeriods';

export interface ForecastWeatherLayerDef {
  settingsKey: ForecastWeatherLayerKey;
  periodSettingsKey: ForecastWeatherPeriodKey;
  sectionTitle: string;
  toggleLabel: string;
  summary: string;
  tileLayer: string;
  sourceLayer: string;
  sourceIdPrefix: string;
  fillLayerIdPrefix: string;
  paint: NonNullable<FillLayerSpecification['paint']>;
  legend: ChoroplethLegendSpec;
  info: string;
}

const BASE_OPACITY = 0.72;

const TEMP_STOPS: ChoroplethColorStop[] = [
  { value: 18, color: '#2563eb' },
  { value: 24, color: '#22c55e' },
  { value: 28, color: '#eab308' },
  { value: 32, color: '#f97316' },
  { value: 36, color: '#dc2626' },
];

const PRECIP_STOPS: ChoroplethColorStop[] = [
  { value: 0, color: '#f8fafc' },
  { value: 5, color: '#bae6fd' },
  { value: 15, color: '#0284c7' },
  { value: 30, color: '#1d4ed8' },
  { value: 60, color: '#312e81' },
];

const WIND_STOPS: ChoroplethColorStop[] = [
  { value: 0, color: '#e0f2fe' },
  { value: 4, color: '#38bdf8' },
  { value: 8, color: '#6366f1' },
  { value: 12, color: '#a855f7' },
  { value: 16, color: '#7e22ce' },
];

function forecastFillPaint(stops: readonly ChoroplethColorStop[]): NonNullable<FillLayerSpecification['paint']> {
  return {
    'fill-color': buildInterpolateFillColor(stops) as ExpressionSpecification,
    'fill-opacity': BASE_OPACITY,
  };
}

export const FORECAST_WEATHER_LAYER_DEFS: ForecastWeatherLayerDef[] = [
  {
    settingsKey: 'forecastWeatherTemp',
    periodSettingsKey: 'forecastWeatherTempPeriods',
    sectionTitle: 'Forecast Temperature',
    toggleLabel: 'Temperature forecast',
    summary: 'Average temperature for each day over the next week.',
    tileLayer: 'weather_forecast_temp',
    sourceLayer: 'weather_forecast_temp',
    sourceIdPrefix: 'ghana-forecast-weather-temp-',
    fillLayerIdPrefix: 'ghana-forecast-weather-temp-fill-',
    info: 'Expected average temperature today. Updates twice daily.',
    paint: forecastFillPaint(TEMP_STOPS),
    legend: { title: 'Forecast temp', unit: '°C', stops: TEMP_STOPS },
  },
  {
    settingsKey: 'forecastWeatherPrecip',
    periodSettingsKey: 'forecastWeatherPrecipPeriods',
    sectionTitle: 'Forecast Precipitation',
    toggleLabel: 'Precipitation forecast',
    summary: 'Expected rainfall for each day over the next week.',
    tileLayer: 'weather_forecast_precip',
    sourceLayer: 'weather_forecast_precip',
    sourceIdPrefix: 'ghana-forecast-weather-precip-',
    fillLayerIdPrefix: 'ghana-forecast-weather-precip-fill-',
    info: 'Expected rainfall today. For planning only. Not official warnings.',
    paint: forecastFillPaint(PRECIP_STOPS),
    legend: { title: 'Forecast rain', unit: 'mm', stops: PRECIP_STOPS },
  },
  {
    settingsKey: 'forecastWeatherWind',
    periodSettingsKey: 'forecastWeatherWindPeriods',
    sectionTitle: 'Forecast Wind',
    toggleLabel: 'Wind speed forecast',
    summary: 'Expected peak wind speed for each day over the next week.',
    tileLayer: 'weather_forecast_wind',
    sourceLayer: 'weather_forecast_wind',
    sourceIdPrefix: 'ghana-forecast-weather-wind-',
    fillLayerIdPrefix: 'ghana-forecast-weather-wind-fill-',
    info: 'Expected peak wind speed today.',
    paint: forecastFillPaint(WIND_STOPS),
    legend: { title: 'Forecast wind', unit: 'm/s', stops: WIND_STOPS },
  },
];

export const FORECAST_WEATHER_LAYER_KEYS = FORECAST_WEATHER_LAYER_DEFS.map((d) => d.settingsKey);

export function forecastWeatherSourceId(def: ForecastWeatherLayerDef, period: string): string {
  return `${def.sourceIdPrefix}${period}`;
}

export function forecastWeatherFillLayerId(def: ForecastWeatherLayerDef, period: string): string {
  return `${def.fillLayerIdPrefix}${period}`;
}

export function forecastWeatherPeriodTilesUrl(tileRoot: string, tileLayer: string, period: string): string {
  const base = tileRoot.replace(/\/$/, '');
  return `${base}/tiles/${tileLayer}/periods/${encodeURIComponent(period)}/{z}/{x}/{y}.pbf`;
}

export function formatForecastDayPill(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00`);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function latestTiledForecastPeriod(periods: string[]): string | undefined {
  if (periods.length === 0) return undefined;
  const sorted = [...periods].sort();
  return sorted[sorted.length - 1];
}
